import { useEffect, useRef } from 'react'

import { deleteAIConversation, updateAIConversation } from '../../../../../api'
import { isApiNotFoundStatus } from '../../../../../utils/api'
import { GretaAIChatStatus } from '../../../GretaAIChat.constants'
import {
  getGretaAIChatChatsWithoutId,
  getGretaAIChatRestoredChatRenamed,
  getGretaAIChatRestoredChatRolledBack,
  getGretaAIChatRestoredChatWithoutId,
  getHasGretaAIChatChatId,
  mapGretaAIChatChat,
} from '../useGretaAIChatChatListLoader.utils'

// Optimistic list edits, rolled back when the request fails.
export function useGretaAIChatChatMutations({
  chats,
  restoredChat,
  setChats,
  setChatsStatus,
  setRestoredChat,
}) {
  const chatsRef = useRef(chats)
  const restoredChatRef = useRef(restoredChat)

  useEffect(() => {
    chatsRef.current = chats
  }, [chats])

  useEffect(() => {
    restoredChatRef.current = restoredChat
  }, [restoredChat])

  function addChat(chat) {
    const mappedChat = mapGretaAIChatChat(chat)

    setChats(current =>
      getHasGretaAIChatChatId(current, mappedChat.id)
        ? current
        : [mappedChat, ...current]
    )
    setChatsStatus(GretaAIChatStatus.Idle)
  }

  function dropChat(id) {
    setChats(current => getGretaAIChatChatsWithoutId(current, id))
    setRestoredChat(current => getGretaAIChatRestoredChatWithoutId(current, id))
  }

  function rollBackRestoredChat(id, previousRestoredChat) {
    setRestoredChat(current =>
      getGretaAIChatRestoredChatRolledBack({
        id,
        previousRestoredChat,
        restoredChat: current,
      })
    )
  }

  async function renameChat(id, title) {
    const previous = chatsRef.current
    const previousRestoredChat = restoredChatRef.current

    setChats(current =>
      current.map(chat => (chat.id === id ? { ...chat, title } : chat))
    )
    setRestoredChat(current =>
      getGretaAIChatRestoredChatRenamed({ id, restoredChat: current, title })
    )

    try {
      await updateAIConversation({ data: { title }, id })

      return true
    } catch (reason) {
      if (isApiNotFoundStatus(reason?.response?.status)) {
        dropChat(id)

        return false
      }

      setChats(previous)
      rollBackRestoredChat(id, previousRestoredChat)

      throw reason
    }
  }

  async function removeChat(id) {
    const previous = chatsRef.current
    const previousRestoredChat = restoredChatRef.current

    dropChat(id)

    try {
      await deleteAIConversation({ id })

      return true
    } catch (reason) {
      if (isApiNotFoundStatus(reason?.response?.status)) {
        return true
      }

      setChats(previous)
      rollBackRestoredChat(id, previousRestoredChat)

      return false
    }
  }

  return { addChat, dropChat, removeChat, renameChat }
}
